import React, { useState, useEffect, useMemo, useRef } from "react";
import DashboardCardWrapper from "../../common/DashboardCardWrapper";
import DashboardCardHeader from "../../common/DashboardCardHeader";
import BorderedIconWrapper from "../../common/BorderIconWrapper";
import { CardContent } from "../../ui/card";
import RiskIcon from "@/assets/icons/risk-stratification.svg";
import { DataTable } from "@/components/common/Table";
import { columns, type RiskDriver } from "./riskStratificationColumn";
import { RiskStratificationDialog } from "./RiskStratificationDialog";
import { Typography } from "../../ui/typography";
import RedUp from "@/assets/svg/risk-stratification-red-up-graph.svg";
import RedDown from "@/assets/svg/risk-stratification-red-down-graph.svg";
import NoChangeRed from "@/assets/svg/noChangeRed.svg";
import NoChangeGreen from "@/assets/svg/noChangeGreen.svg";
import GreenUp from "@/assets/svg/risk-stratification-green-up-graph.svg";
import GreenDown from "@/assets/svg/risk-stratification-green-down-graph.svg";
import api from "@/axios";
import { routes } from "@/utils/urls";
import { useFetchCard } from "@/hooks/useFetchCard";
import { RootState } from "@/store/store";
import { useSelector } from "react-redux";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatDate } from "@/constants/utilityFunctions";
import { useDashboardCardFavorite } from "@/hooks/useDashboardCardFavorite";
import {
  transformChartData,
  getAvailableYears,
  type RiskChartApiResponse,
} from "@/utils/riskChartUtils";
import type { RiskPoint } from "./RiskStratificationChart";

interface RiskStratificationResponse {
  status: number;
  data: {
    lastUpdated: string;
    riskScore: number;
    riskCategory: string; // "Low", "Medium", or "High"
    riskDrivers: {
      name: string;
      displayName: string;
      trend: string;
      color: string; // "green", "yellow", "amber", or "red"
    }[];
    chartData: {
      date: string;
      score: number;
    }[];
  };
}

const RISK_SCORE_THRESHOLDS = {
  medium: 30,
  high: 60,
} as const;

const RISK_CHART_THRESHOLDS = {
  low: 0,
  medium: RISK_SCORE_THRESHOLDS.medium,
  high: RISK_SCORE_THRESHOLDS.high,
  max: 100,
} as const;

const DUMMY_CHART_VARIANTS = {
  singleReading: [
    { date: "Q1 2025", score: 29 },
    { date: "Q2 2025", score: 42 },
    { date: "Q3 2025", score: null },
    { date: "Q4 2025", score: 81 },
    { date: "Q5 2026", score: 67 },
    { date: "Q6 2026", score: 52 },
    { date: "Q7 2026", score: 28 },
    { date: "Q8 2026", score: 10 },
  ],
  singleYear: [
    { date: "Q1 2025", score: 52 },
    { date: "Q2 2025", score: 33 },
    { date: "Q3 2025", score: 50 },
    { date: "Q4 2025", score: 45 },
  ],
} as const;

const ACTIVE_DUMMY_SCENARIO: keyof typeof DUMMY_CHART_VARIANTS =
  "singleReading";

const deriveCategoryFromScore = (score: number): string => {
  if (score >= RISK_SCORE_THRESHOLDS.high) {
    return "High";
  }
  if (score >= RISK_SCORE_THRESHOLDS.medium) {
    return "Medium";
  }
  return "Low";
};

// Custom hook to detect text truncation
const useIsTruncated = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (ref.current) {
        setIsTruncated(ref.current.scrollWidth > ref.current.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener("resize", checkTruncation);

    return () => {
      window.removeEventListener("resize", checkTruncation);
    };
  }, []);

  return { ref, isTruncated };
};

interface RiskStratificationCardProps {
  shouldBlink?: boolean;
}

const RiskStratificationCard: React.FC<RiskStratificationCardProps> = ({
  shouldBlink = false,
}) => {
  const { cardVariant, isFavourite, toggleFavourite } =
    useDashboardCardFavorite("RISK_STRATIFICATION");
  const [isShowDialog, setIsShowDialog] = useState<boolean>(false);
  const [chartApiData, setChartApiData] = useState<
    RiskChartApiResponse["data"] | null
  >(null);
  const [chartLoading, setChartLoading] = useState<boolean>(false);
  const [chartError, setChartError] = useState<string | null>(null);

  // Use the custom hook for both title and subtitle
  const titleTruncation = useIsTruncated();
  const subtitleTruncation = useIsTruncated();

  const memberId =
    useSelector((state: RootState) => state.search.selectedPatient?.memberId) ||
    "";

  // Fetch card data using useFetchCard hook
  const {
    data: riskStratificationResponse,
    loading,
    error,
    isErrorRed,
  } = useFetchCard<RiskStratificationResponse["data"]>({
    endpoint: `${routes.cards.riskStratification("").replace(/\/$/, "")}`,
    memberId,
    method: "POST",
    requestBody: { view: "card" },
    processData: (rawData: unknown) =>
      (rawData as RiskStratificationResponse).data,
  });

  // Derive card data from hook response
  const riskScore = riskStratificationResponse?.riskScore || 0;
  const riskCategory = riskStratificationResponse?.riskCategory || "High";
  const lastUpdatedDate = riskStratificationResponse?.lastUpdated || "";

  const riskDriversData = useMemo(() => { // NOSONAR
    if (
      !riskStratificationResponse?.riskDrivers ||
      riskStratificationResponse.riskDrivers.length === 0
    ) {
      return [];
    }
    return riskStratificationResponse.riskDrivers.map((item, index) => {
      const colorLower = String(item.color || "").toLowerCase();
      const trend = String(item.trend || "").toLowerCase();
      const isGreen = colorLower === "green";

      let trendImage: string;
      if (isGreen) {
        if (trend === "up") {
          trendImage = GreenUp;
        } else if (trend === "down") {
          trendImage = GreenDown;
        } else if (trend === "no change") {
          trendImage = NoChangeGreen;
        } else {
          trendImage = "-";
        }
      } else {
        if (trend === "no change") {
          trendImage = NoChangeRed;
        } else if (trend === "up") {
          trendImage = RedUp;
        } else {
          trendImage = RedDown;
        }
      }

      return {
        id: index + 1,
        riskDriver: item.displayName,
        trend: trend,
        trendImage: trendImage,
        color: item.color,
      };
    });
  }, [riskStratificationResponse]);

  const fallbackChartData = useMemo(
    () =>
      DUMMY_CHART_VARIANTS[ACTIVE_DUMMY_SCENARIO].map((point) => ({
        ...point,
      })),
    []
  );

  // Fetch chart data when dialog opens
  useEffect(() => {
    const fetchChartData = async () => {
      if (!isShowDialog || !memberId) {
        return;
      }

      setChartLoading(true);
      setChartError(null);

      try {
        const res = await api.post(routes.cards.riskStratification(memberId), {
          view: "chart",
        });
        const data = res.data as RiskChartApiResponse;

        if (data && data.data) {
          setChartApiData(data.data);
        } else {
          setChartError("No chart data available.");
        }
      } catch (err) {
        console.error("Failed to fetch risk chart data:", err);
        setChartError("Failed to load chart data.");
      } finally {
        setChartLoading(false);
      }
    };

    fetchChartData();
  }, [isShowDialog, memberId]);

  // Transform chart data from API
  const transformedChartData = useMemo<RiskPoint[]>(() => {
    if (chartApiData?.chartData) {
      return transformChartData(chartApiData.chartData);
    }
    return [];
  }, [chartApiData]);

  const hasLiveChartData = transformedChartData.length > 0;
  const dialogChartData = hasLiveChartData
    ? transformedChartData
    : fallbackChartData;
  let displayedRiskScore = dialogChartData[dialogChartData.length - 1]?.score || 0;
  if (chartApiData) {
    displayedRiskScore = chartApiData.currentRiskScore;
  } else if (hasLiveChartData) {
    displayedRiskScore = riskScore;
  }

  let displayedRiskCategory = deriveCategoryFromScore(displayedRiskScore);
  if (chartApiData) {
    displayedRiskCategory = chartApiData.currentRiskCategory;
  } else if (hasLiveChartData) {
    displayedRiskCategory = riskCategory;
  }

  // Determine chip variant based on risk category
  const getChipVariant = (
    category: string
  ): "success" | "warning" | "danger" => {
    const lowerCategory = category.toLowerCase();
    if (lowerCategory === "low") return "success";
    if (lowerCategory === "medium") return "warning";
    return "danger";
  };

  const subtitleText = (() => {
    if (loading) return "Loading...";
    if (error) return "-";
    return formatDate(lastUpdatedDate || "-");
  })();

  return (
    <>
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md h-fit'>
        <DashboardCardWrapper variant={cardVariant} shouldBlink={shouldBlink}>
          <DashboardCardHeader
            icon={
              <BorderedIconWrapper>
                <img
                  src={RiskIcon}
                  alt='Risk Stratification'
                  className='w-4 h-4'
                />
              </BorderedIconWrapper>
            }
            className='h-[56px]'
            title={
              <div>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className='max-w-full'>
                        <Typography
                          variant='semibold-16'
                          className='text-grey-800 dark:text-white truncate whitespace-nowrap max-w-full'
                          style={{
                            cursor: titleTruncation.isTruncated
                              ? "pointer"
                              : "text",
                          }}
                          ref={titleTruncation.ref}>
                          Risk Stratification
                        </Typography>
                      </div>
                    </TooltipTrigger>
                    {titleTruncation.isTruncated && (
                      <TooltipContent
                        side='top'
                        align='start'
                        className='max-w-xs break-words z-50 bg-gray-700 text-grey-100'>
                        <Typography
                          className=' bg-gray-700 text-grey-100'
                          variant='regular-12'>
                          Risk Stratification
                        </Typography>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className='max-w-full'>
                        <Typography
                          ref={subtitleTruncation.ref}
                          className='text-grey-600 dark:text-grey-500 font-normal truncate whitespace-nowrap max-w-full cursor-pointer'
                          style={{
                            fontFamily: "Inter",
                            fontWeight: 400,
                            fontStyle: "normal",
                            fontSize: "12px",
                            lineHeight: "20px",
                            letterSpacing: "0px",
                          }}>
                          Last updated on {subtitleText}
                        </Typography>
                      </div>
                    </TooltipTrigger>
                    {subtitleTruncation.isTruncated && (
                      <TooltipContent
                        side='top'
                        align='start'
                        className='max-w-xs break-words z-50 bg-gray-700 text-grey-100'>
                        <Typography
                          className=' bg-gray-700 text-grey-100 text-right'
                          variant='regular-12'>
                          Last updated on {subtitleText}
                        </Typography>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            }
            chip={{
              key: (
                <>
                  <span className='inline md:inline lg:inline'>Risk Score</span>
                </>
              ),
              value: `${riskScore} %`,
              variant: getChipVariant(riskCategory),
              onClick: () => setIsShowDialog(true),
            }}
            showStar={true}
            isFavourite={isFavourite}
            onToggleFavourite={toggleFavourite}
          />
          <CardContent className='p-4 lg:h-[312px]'>
            {error ? (
              <div className='flex items-center justify-center h-full lg:h-[312px] md:h-[312px]'>
                <Typography
                  variant='medium-14'
                  className={`${
                    isErrorRed
                      ? "text-red-500 dark:text-red-400"
                      : "text-grey-600 dark:text-grey-500"
                  } font-medium`}>
                  {error}
                </Typography>
              </div>
            ) : (
              <DataTable<RiskDriver>
                data={{
                  data: riskDriversData,
                  total: riskDriversData.length,
                }}
                columns={columns}
                height='auto'
                maxHeight={"308px"}
                isLoading={loading}
                skeletonCount={5}
              />
            )}
          </CardContent>
        </DashboardCardWrapper>
      </div>


      <RiskStratificationDialog
        riskScore={displayedRiskScore}
        riskCategory={displayedRiskCategory}
        data={transformedChartData}
        thresholds={RISK_CHART_THRESHOLDS}
        open={isShowDialog}
        onOpenChange={() => setIsShowDialog(false)}
        currentYear={chartApiData?.dataRange?.currentYear}
        availableYears={
          chartApiData?.chartData
            ? getAvailableYears(chartApiData.chartData)
            : []
        }
        chartLoading={chartLoading}
        chartError={chartError}
        lastUpdated={lastUpdatedDate}
      />
    </>
  );
};

export default RiskStratificationCard;
